import { useEffect, useRef, useState } from "react";
import Grid, { GridColumn, GridHeader, GridRow } from "../../components/Grid/Grid";
import WebService from "../../utility/WebService";
import PageTitle from "../../components/PageTitle/PageTitle";
import HelperService from "../../utility/HelperService";
import { permissionsInterface } from "../onBoarding/onBoardingTypes";
import { useSelector } from "react-redux";
import ExpandableGrid, { ExpandableRow } from "../../components/Grid/ExpandableGrid";
import PlotDatePicker from "../../components/PlotDatePicker/PlotDatePicker";

const expenseHeaders: GridHeader[] = [
  {
    title: "Project name",
    class: "text-center",
  },
  {
    title: "Expense Type",
    class: "text-center",
  },
  {
    title: "Amount",
    class: "text-center",
  },
  {
    title: "Payment mode",
    class: "text-center",
  },
  {
    title: "Comment",
    class: "text-center",
  }
];

const receiptHeaders: GridHeader[] = [
  {
    title: "Receipt ID",
    class: "text-center",
  },
  {
    title: "Project name",
    class: "text-center",
  },
  {
    title: "Total Cost",
    class: "text-center",
  },
  {
    title: "Paying Amount",
    class: "text-center",
  },
  {
    title: "Payment mode",
    class: "text-center",
  },
  {
    title: "Comment",
    class: "text-center",
  }
]

const SummaryReport = () => {
  const nextOffset = useRef<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [receiptRows, setReceiptRows] = useState<ExpandableRow[]>([]);
  const [expensesRows, setExpensesRows] = useState<ExpandableRow[]>([]);
  const token = useSelector((state: any) => state.OnBoardingReducer);
  const [permissions, setPermissions] = useState<permissionsInterface | null>(null);
  const [pressedKeys, setPressedKeys] = useState(new Set());
  const [showHiddenFields, setShowHiddenFields] = useState<boolean>(false);
  const [date, setDate] = useState<Date>(new Date());

  useEffect(() => {
    // getSummaryReportList();
    if (token && token.token && token.permissions && !!token.permissions.length) {
      setPermissions(token.permissions.find((item: any) => item.name === "SUMMARY_REPORT"));
    }
  }, [token]);

  useEffect(() => {
    if (date) {
      getSummaryReportList();
    }
  }, [date]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [pressedKeys]);

  useEffect(() => {
    showHiddenFields && getSummaryReportList();
  }, [showHiddenFields]);

  const handleKeyDown = (e: any) => {
    const key = e.key ? e.key.toLowerCase() : "";
    setPressedKeys((prev: any) => new Set([...prev, key]));
  };

  const handleKeyUp = (e: any) => {
    const key = e.key ? e.key.toLowerCase() : "";
    // Check for ctrl+p+t
    if (pressedKeys.has('control') && pressedKeys.has('alt') && pressedKeys.has('o') && pressedKeys.has('p')) {
      setShowHiddenFields(true);
    }
    // Remove the key from the set
    setPressedKeys((prev: any) => { const updated = new Set(prev); updated.delete(key); return updated; });
  };

  const getSummaryReportList = () => {
    setIsLoading(true);
    WebService.getAPI({
      action: `summary/report?date=${date}`,
      body: null,
      is_session: showHiddenFields
    })
      .then((res: any) => {
        setIsLoading(false);

        let receipt_emp: any = [{ header: getHeader(res.receipt_list), isExpand: false, data: [], key: new Date().getTime(), class: "d-flex" }];
        let data: any[] = [];
        for (let i in res.receipt_list) {
          data.push({
            data: [
              { value: res.receipt_list[i].receipt_id ? res.receipt_list[i].receipt_id : "N/A" },
              { value: res.receipt_list[i].project_name ? res.receipt_list[i].project_name : "N/A" },
              { value: HelperService.getCurrencyFormatter(res.receipt_list[i].total_cost) },
              { value: HelperService.getCurrencyFormatter(res.receipt_list[i].paying_amount) },
              { value: res.receipt_list[i].payment_mode ? HelperService.capitalizeFirstLetter(res.receipt_list[i].payment_mode) : "N/A" },
              { value: res.receipt_list[i].comment ? res.receipt_list[i].comment : "N/A", }
            ]
          });
        };
        receipt_emp[0].data = data;
        setReceiptRows(receipt_emp);

        let expenses_emp: any = [{ header: getExpensesHeader(res.expense_list), isExpand: false, data: [], key: new Date().getTime(), class: "d-flex" }];
        let expenses_data: any[] = [];
        for (let i in res.expense_list) {
          expenses_data.push({
            data: [
              { value: res.expense_list[i]?.project?.project_name ? res.expense_list[i].project?.project_name : "N/A", },
              { value: res.expense_list[i]?.expense_type?.name ? res.expense_list[i]?.expense_type?.name : "N/A", },
              { value: res.expense_list[i].amount ? res.expense_list[i].amount : "N/A", },
              { value: res.expense_list[i].payment_mode_detail ? `${res.expense_list[i].payment_mode_detail?.name} ${res.expense_list[i].payment_mode_detail?.bank_name ? `(${res.expense_list[i].payment_mode_detail?.bank_name})` : ""}` : "N/A", },
              { value: res.expense_list[i].comment ? res.expense_list[i].comment : "N/A", }
            ]
          });
        };
        expenses_emp[0].data = expenses_data;
        setExpensesRows(expenses_emp);

      }).catch((e) => { setIsLoading(false); });
  };

  const getHeader = (data: any) => {
    return (
      <div className="d-flex justify-content-between align-items-center w-100">
        <div className="fw-500">Reciept : {data && data.length > 0 && data.reduce((acc: any, item: any) => acc + item.paying_amount, 0)}</div>
      </div>
    );
  };

  const getExpensesHeader = (data: any) => {
    return (
      <div className="d-flex justify-content-between align-items-center w-100">
        <div className="fw-500">Expenses : {data && data.length > 0 && data.reduce((acc: any, item: any) => acc + item.amount, 0)}</div>
      </div>
    );
  };


  return (
    <>
      {permissions && permissions.can_read && <div className="app-page page-dashboard">
        <div className="d-flex justify-content-between align-items-center ">
          <PageTitle title="Cash Book Summary" backArrow={true} active={showHiddenFields} />
          <PlotDatePicker selected={date} maxData={new Date()} onChange={(date: Date) => setDate(date)} />
        </div>
        <div className="table-card">
          <h6>Receipt Summary</h6>
          <ExpandableGrid
            rows={receiptRows}
            headers={receiptHeaders}
            errorMessage={"No Receipt Found"}
            ShowLoader={isLoading}
          />

          <h6>Expenses Summary</h6>
          <ExpandableGrid
            rows={expensesRows}
            headers={expenseHeaders}
            errorMessage={"No Expense Found"}
            ShowLoader={isLoading}
          />
        </div>
      </div>}
    </>
  );
};

export default SummaryReport;
